import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { ComboboxField } from '@clay-run/glaze/combobox-field';
import { InputField } from '@clay-run/glaze/input-field';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { Text } from '@clay-run/glaze/text';
import { TextareaField } from '@clay-run/glaze/textarea-field';

import { useApi } from '../../_shared/hooks';
import type { Campaign } from '../../_shared/launcher-campaign.types';
import {
  canConfirmCreatePlay,
  createPlayErrorMessage,
  filterPlaybooksByQuery,
  formAfterPlaybookCreated,
  initialCreatePlayForm,
  normalizeDescription,
  playbookOptionLabel,
  type CreatePlayForm,
  type CreatePlayMode,
} from '../create-play-sidebar-logic';
import { createPlay, createPlaybook } from '../mutations';
import { libraryQueryKey } from '../query-keys';
import { usePlaybooksLibrary } from '../use-playbooks-library';
import { invalidatePlayLists } from '../use-plays-library';
import { PushDrawer } from './PushDrawer';

export interface CreatePlaySidebarProps {
  /** The campaign this Play is being planned for — `campaign.id` IS the SFDC campaign id (see `plays-actions.tsx`'s doc). */
  campaign: Campaign;
  /** Header X and footer Cancel. Nothing has been persisted when this fires (unless a `createPlaybook` already succeeded — see `handleConfirm` below). */
  onClose: () => void;
  /** Fires once the real Play exists; the parent navigates. */
  onCreated: (result: { playId: string; campaignSfdcId: string }) => void;
}

/**
 * The "Create Play" sidebar: choose an existing Playbook from the library or
 * create a new one inline, then create a real, persisted Play against it
 * (`mutations.createPlay`) — replacing the old client-only
 * `createDraftPlay` fake. Mounted by `PlaybooksHome` when a campaign is
 * targeted (see that file's `createPlayTarget` state).
 *
 * If the inline "Create new Playbook" leg succeeds but the subsequent
 * `createPlay` call then fails, the empty Playbook legitimately exists in
 * the library (same as creating one from the Config page) — the form is
 * left in `existing` mode with it preselected so a retry never creates a
 * duplicate, and Cancel at that point leaves the Playbook but no Play. That
 * is an accepted, documented edge case, not a bug.
 */
export function CreatePlaySidebar({ campaign, onClose, onCreated }: CreatePlaySidebarProps) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const library = usePlaybooksLibrary();
  const playbooks = library.data?.playbooks ?? [];

  const [form, setForm] = useState<CreatePlayForm>(initialCreatePlayForm);
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtered = filterPlaybooksByQuery(playbooks, query);
  const selected = playbooks.find((pb) => pb.id === form.selectedPlaybookId) ?? null;

  async function handleConfirm() {
    if (!canConfirmCreatePlay(form, submitting)) return;
    setSubmitting(true);
    setError(null);
    try {
      let playbookId = form.selectedPlaybookId;
      if (form.mode === 'new') {
        const created = await createPlaybook(apiFetch, form.newName.trim(), normalizeDescription(form.newDescription));
        playbookId = created.id;
        void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
        setForm((f) => formAfterPlaybookCreated(f, created.id));
        setQuery(form.newName.trim());
      }
      if (playbookId === null) throw new Error('Pick a Playbook.');
      const play = await createPlay(apiFetch, campaign.id, playbookId);
      await invalidatePlayLists(queryClient, campaign.id);
      onCreated({ playId: play.id, campaignSfdcId: campaign.id });
    } catch (err) {
      setError(createPlayErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PushDrawer
      title="Create Play"
      subtitle={campaign.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={!canConfirmCreatePlay(form, submitting)}
            loading={submitting}
          >
            Create Play
          </Button>
        </>
      }
    >
      <SegmentedControl
        value={form.mode}
        onValueChange={(mode: CreatePlayMode) => {
          setForm((f) => ({ ...f, mode }));
          setError(null);
        }}
      >
        <SegmentedControlOption value="existing">Choose existing</SegmentedControlOption>
        <SegmentedControlOption value="new">Create new</SegmentedControlOption>
      </SegmentedControl>

      {library.isError && <Alert status="error">Failed to load Playbooks. Please try again.</Alert>}

      {form.mode === 'existing' ? (
        <>
          <ComboboxField
            label="Playbook"
            aria-label="Choose a Playbook"
            placeholder="Search Playbooks…"
            items={filtered}
            itemToStringLabel={playbookOptionLabel}
            value={selected}
            onValueChange={(pb) => {
              setForm((f) => ({ ...f, selectedPlaybookId: pb?.id ?? null }));
              if (pb) setQuery(playbookOptionLabel(pb));
            }}
            inputValue={query}
            onInputValueChange={setQuery}
            by="id"
            loading={library.isLoading}
          >
            {filtered.map((pb) => (
              <ComboboxOption key={pb.id} value={pb}>
                {playbookOptionLabel(pb)}
              </ComboboxOption>
            ))}
          </ComboboxField>
          {library.isSuccess && playbooks.length === 0 && (
            <Text size="sm" variant="tertiary">
              No Playbooks in the library yet — switch to &quot;Create new&quot;.
            </Text>
          )}
          {selected?.description && (
            <Text size="xs" variant="tertiary">
              {selected.description}
            </Text>
          )}
        </>
      ) : (
        <>
          <InputField
            label="Playbook name"
            value={form.newName}
            onChange={(e) => setForm((f) => ({ ...f, newName: e.target.value }))}
            autoFocus
          />
          <TextareaField
            label="Description (optional)"
            value={form.newDescription}
            onValueChange={(newDescription) => setForm((f) => ({ ...f, newDescription }))}
          />
        </>
      )}

      {error && <Alert status="error">{error}</Alert>}
    </PushDrawer>
  );
}
